/**
 * 문제집 관리 API 서비스
 * - ZIP 파일 업로드 (Excel + 정답 이미지)
 * - 문제집 목록 조회
 * - 문제집 상세 조회
 */

import axios from '../config/axios';
import type {
  WorkbookUploadResponse,
  Workbook,
  Problem,
  WorkbookListParams,
  WorkbookListResponse,
} from '../types/workbook';

/**
 * 문제집 서비스 클래스
 */
class WorkbookService {
  /**
   * ZIP 파일 업로드 (Excel + 정답 이미지)
   * @param file - ZIP 파일 (Excel + answers/ 폴더)
   * @returns 업로드 결과 (성공/실패, 문제 개수, 이미지 개수)
   */
  async uploadWorkbookZip(file: File): Promise<WorkbookUploadResponse> {
    try {
      // FormData 생성
      const formData = new FormData();
      formData.append('file', file);

      // API 호출 (admin 전용 엔드포인트)
      const response = await axios.post<WorkbookUploadResponse>(
        '/admin/upload-workbook-zip',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      // 에러 응답 처리
      if (error.response?.data) {
        return error.response.data;
      }

      // 네트워크 에러 등
      return {
        success: false,
        errors: ['서버 연결 실패'],
      };
    }
  }

  /**
   * 문제집 목록 조회 (역할별 권한 처리)
   * @param params - 검색/필터 파라미터
   * @returns 문제집 목록 및 페이지네이션 정보
   */
  async getWorkbooks(params: WorkbookListParams = {}): Promise<WorkbookListResponse> {
    // 백엔드에서 역할별로 자동 필터링하므로 /workbooks 엔드포인트 사용
    const response = await axios.get<{ success: boolean; workbooks: Workbook[]; count: number }>(
      '/workbooks',
      {
        params,
      }
    );

    // 응답 형식 통일
    return {
      workbooks: response.data.workbooks,
      total: response.data.count,
      page: params.page || 1,
      page_size: params.page_size || 50,
      total_pages: Math.ceil(response.data.count / (params.page_size || 50)),
    };
  }

  /**
   * 문제집 상세 조회 (역할별 권한 처리)
   * @param workbookName - 문제집명
   * @returns 문제집 정보
   */
  async getWorkbook(workbookName: string): Promise<Workbook> {
    const response = await axios.get<{ success: boolean; workbook: Workbook }>(
      `/workbooks/${encodeURIComponent(workbookName)}`
    );
    return response.data.workbook;
  }

  /**
   * 문제집의 문제 목록 조회
   * @param workbookName - 문제집명
   * @param page - 페이지 번호 (optional, 특정 페이지만 조회)
   * @param limit - 최대 조회 개수 (optional, 기본값 1000)
   * @returns 문제 목록
   */
  async getProblems(workbookName: string, page?: number, limit: number = 2000): Promise<Problem[]> {
    const response = await axios.get<{
      success: boolean;
      problems: Problem[];
      count: number;
    }>(`/workbooks/${encodeURIComponent(workbookName)}/problems`, {
      params: { page, limit },
    });
    return response.data.problems;
  }

  /**
   * 문제집 삭제
   * @param workbookName - 문제집명
   */
  async deleteWorkbook(workbookName: string): Promise<void> {
    await axios.delete(`/workbooks/${encodeURIComponent(workbookName)}`);
  }
}

export const workbookService = new WorkbookService();
